import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AcuerdoIndirectoControllerService,
  AcuerdoIndirectoDTO,
  DocumControllerService,
  TipoDocumentoControllerService,
  TipoDocumentoModel,
} from 'api-rest';
import { SpinnerService } from '../../services/spinner.service';
import { SnackbarService } from '../../services/snackbar.service';

const TIPO_DOC_CONTRATO = 1;
const ENTITY_TYPE_ACUERDO_INDIRECTO = 'ACUERDOS_GESTORES';

@Component({
  selector: 'managers-agreements-documents-dialog',
  templateUrl: './documents-dialog.component.html',
  styleUrls: ['./documents-dialog.component.scss']
})
export class DocumentsDialogComponent implements OnInit {
  formGroup!: FormGroup;
  acuerdoIndirectoInfo!: AcuerdoIndirectoDTO;
  tiposDocumento: TipoDocumentoModel[] = [];
  adjuntosBlob: Array<File> = [];
  botonEstado: boolean = false;
  flagTooltip: boolean = false;

  containerAcuerdosGestores: string = this.environment.documentContainerAcuerdosGestores;

  constructor(
    @Inject('environment') private environment: any,
    public dialogRef: MatDialogRef<DocumentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private documControllerSrv: DocumControllerService,
    private tipoDocumentoControllerSrv: TipoDocumentoControllerService,
    private acuerdoIndirectoControllerSrv: AcuerdoIndirectoControllerService,
    public spinnerSrv: SpinnerService,
    private snackbarSrv: SnackbarService
  ) {
    this.acuerdoIndirectoInfo = this.data.acuerdoIndirectoInfo;

    this.formGroup = this.formBuilder.group({
      tipoDocumento: [{ value: null, disabled: false }, { validators: Validators.compose([Validators.required]) }],
      fechaFirma: [{ value: null, disabled: false }],
      descripcion: [{ value: null, disabled: false }, { validators: Validators.compose([Validators.maxLength(255)]) }]
    });
  }

  ngOnInit(): void {
    this.cargarTiposDocumento();
    this.cargarTooltip();
  }

  cargarTooltip() {
    this.formGroup.get('tipoDocumento')?.valueChanges.subscribe((value: any) => {
      this.flagTooltip = value == 4;
      const fechaFirmaControl = this.formGroup.get('fechaFirma');
      if (value != TIPO_DOC_CONTRATO) {
        fechaFirmaControl?.setValue(null);
      }
    });
  }

  cargarTiposDocumento(): void {
    this.tipoDocumentoControllerSrv.listarTipoDocumentos()
      .subscribe({
        next: (response: TipoDocumentoModel[]) => {
          // Filtrar solo los documentos con ids 1, 4 y 5 (contrato, otros, adenda)
          this.tiposDocumento = response.filter(doc => [1, 4, 5].includes(Number(doc.id)));
        },
        error: err => {
          console.log(err);
          this.snackbarSrv.showSnackBar('Error al cargar datos', 'error');
        }
      });
  }

  cerrar(): void {
    this.dialogRef.close(null);
  }

  guardar(): void {
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
    if (this.formGroup.valid) {
      if (this.adjuntosBlob?.length > 0) {
        let id = this.acuerdoIndirectoInfo.id! ?? null;
        let typeDoc = this.formGroup.controls['tipoDocumento'].value! ?? null;
        let docDesc = this.formGroup.controls['descripcion'].value! ?? '';
        let fechaFirma = this.formGroup.controls['fechaFirma'].value;

        this.setBotonEstado(true);
        this.spinnerSrv.loadingShow();
        this.documControllerSrv
          .uploadDoc(
            this.containerAcuerdosGestores,
            typeDoc,
            id!,
            ENTITY_TYPE_ACUERDO_INDIRECTO,
            this.adjuntosBlob[0]?.name,
            this.adjuntosBlob[0],
            docDesc
          )
          .subscribe({
            next: () => {
              if (typeDoc === TIPO_DOC_CONTRATO) {
                let acuerdoUpdate: AcuerdoIndirectoDTO = { ...this.acuerdoIndirectoInfo };
                try {
                  acuerdoUpdate.fechaFirmaAcuerdo = fechaFirma
                    ? new Date(fechaFirma).toISOString()
                    : new Date().toISOString();
                } catch (error) {
                  console.error('Error procesando la fecha: ', error);
                }
                // TODO: confirmar con negocio si al firmar el acuerdo debe además transicionar el estado del acuerdo indirecto.
                this.acuerdoIndirectoControllerSrv.updateById(id!, acuerdoUpdate).subscribe({
                  next: () => {
                    this.spinnerSrv.loadingHide();
                    this.snackbarSrv.showSnackBar('Documento subido satisfactoriamente', 'success');
                    this.dialogRef.close(true);
                  },
                  error: (err: { message: any }) => {
                    this.spinnerSrv.loadingHide();
                    this.setBotonEstado(false);
                    this.snackbarSrv.showSnackBar('Documento subido pero hubo un error al actualizar el acuerdo', 'error');
                  },
                });
              } else {
                this.spinnerSrv.loadingHide();
                this.snackbarSrv.showSnackBar('Documento subido satisfactoriamente', 'success');
                this.dialogRef.close(true);
              }
            },
            error: err => {
              this.spinnerSrv.loadingHide();
              this.setBotonEstado(false);
              this.snackbarSrv.showSnackBar('Error al subir documento', 'error');
            }
          });
      }
    }
  }
  controlHasError(controlName: string, errorName: string) {
    return this.formGroup.controls[controlName].hasError(errorName)
      && this.formGroup.controls[controlName].touched;
  }

  attachAdjunto(archivo: File | null): void {
    if (archivo != null) {
      if (archivo.size === 0) {
        this.snackbarSrv.showSnackBar(
          'No se puede subir un fichero vacío',
          'error'
        );
      } else if (archivo.size > 12 * 1024 * 1024) { // 5 MB
        this.snackbarSrv.showSnackBar(
          'El fichero supera el tamaño máximo permitido de 12 MB',
          'error'
        );
      } else {
        this.adjuntosBlob.push(archivo);
      }
    }
  }

  setBotonEstado(estado: boolean) {
    return this.botonEstado = estado;
  }

}

